import React, { useEffect, useCallback, useLayoutEffect } from "react";
import {
  useTable,
  useFilters,
  useSortBy,
  useResizeColumns,
  useBlockLayout,
  useRowSelect,
  useGlobalFilter,
  useExpanded,
  useGroupBy,
} from "react-table";
import { VariableSizeList, FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { Scrollbars } from "react-custom-scrollbars-2";

import IndeterminateCheckbox from "../IndeterminateCheckbox.jsx";
import { useLocalStorage } from "../../../hooks/index.js";
import { FORM_STATUS, ROW_HEIGHT_PADDING, SORT_TOGGLE_WIDTH } from "./constants.js";
import { GlobalFilterPortal } from './GlobalFilter.jsx';

const SELECTION_COLUMN_WIDTH = 40;
const DEFAULT_ROW_CONTENT_HEIGHT = 22;
const AUTOSIZER_MARGIN = 20;
const REACT_TABLE_STORAGE_KEY = "ReactTable";
const DEFAULT_MAX_COLUMN_WIDTH = 500;
export const REACT_TABLE_FILTER_STORAGE_KEY = "ReactTable_filter_storage";


export const RowsCount = ({rowsCount, resultCount}) => {
  let isFiltered = !(rowsCount == resultCount)
  return isFiltered ? `${rowsCount} ${I18n.t("table_filtered_count_text")} ${resultCount}` : `${resultCount} ${I18n.t("entries")}`
}

export const actionCellEvents = (allowClick = false) => ({
  onClick: (e, row) => {
    if (!allowClick) {
      e.preventDefault();
    }

    e.stopPropagation();
  },
  onMouseOver: (e) => jQuery(e.target).closest(".tr").addClass("action-hover"),
  onMouseOut: (e) => jQuery(e.target).closest(".tr").removeClass("action-hover"),
});

const sortByLabel = (rowA, rowB, columnId, desc) => {
  const labelA = (rowA.values[columnId] || {}).label || "";
  const labelB = (rowB.values[columnId] || {}).label || "";

  return labelA.localeCompare(labelB);
};

const defaultSort = (rowA, rowB, columnId, desc) => {
  const valueA = rowA.values[columnId] || "";
  const valueB = rowB.values[columnId] || "";

  return valueA.toString().localeCompare(valueB);
};

const numberSort = (rowA, rowB, columnId, desc) => {
  let valueA = parseFloat(rowA.values[columnId] || 0);
  let valueB = parseFloat(rowB.values[columnId] || 0);
  valueA = isNaN(valueA) ? -1 : valueA;
  valueB = isNaN(valueB) ? -1 : valueB;

  if (valueA > valueB) return 1;
  if (valueB > valueA) return -1;
  return 0;
};

const defaultAggregations = {
  sum: (values, aggregatedValues) => {
    return aggregatedValues.reduce(
      (sum, next) => sum + (isNaN(parseFloat(next, 10)) ? 0 : parseFloat(next, 10)),
      0
    );
  },
  first: (values, aggregatedValues) => values[0],
  compact_first: (values, aggregatedValues) => {
    values
    return values.filter(v => v)[0]
  },
};

const CustomScrollbars = ({ onScroll, forwardedRef, style, children }) => {
  const refSetter = useCallback(
    (scrollbarsRef) => {
      if (scrollbarsRef) {
        forwardedRef(scrollbarsRef.view);
      } else {
        forwardedRef(null);
      }
    },
    [forwardedRef]
  );

  return (
    <Scrollbars ref={refSetter} style={{ ...style, overflow: "hidden" }} onScroll={onScroll}>
      {children}
      {children.props.children.length == 0 && <div className="tr even" style={{ padding: "8px" }}>{I18n.t('dt')['empty_table']}</div>}
    </Scrollbars>
  );
};

export const CustomScrollbarsVirtualList = React.forwardRef((props, ref) => (
  <CustomScrollbars {...props} forwardedRef={ref} />
));

const includesOptionFilter = (rows, id, filterValue) => {
  return rows.filter((row) => {
    // Try to use attr 'value' of cell value, if it's not an object default to the whole value
    const { value = row.values[id] } = row.values[id] || {};
    // If columns has an empty value, use null for filtering
    return filterValue.includes(value || value === 0 ? value : null);
  });
};

includesOptionFilter.autoRemove = (val) => !val || !val.length;

const dateRangeFilter = (rows, id, filterValue) => {
  return rows.filter((row) => {
    // Try to use attr 'value' of cell value, if it's not an object default to the whole value
    const { value = row.values[id] } = row.values[id];
    // If columns has an empty value, use null for filtering

    return value && dayjs(value).isBetween(filterValue[0], filterValue[1], "days", "[]");
  });
};

dateRangeFilter.autoRemove = (val) => !val || !val.length;

const isNotEmpty = (rows, id, filterValue) => {
  return rows.filter((row) => {
    // Try to use attr 'value' of cell value, if it's not an object default to the whole value
    const { value = row.values[id] } = row.values[id] || {};
    // If columns has an empty value, use null for filtering
    return value.length > 0 ? filterValue.filter(Boolean).length > 0 : filterValue.includes(null);
  });
};

isNotEmpty.autoRemove = (val) => !val || !val.length;


// Table component which can be used as a standalone or with TableContainer
const Table = ({
  data,
  columns: propColumns,
  rowElem,
  itemDefaultSize = DEFAULT_ROW_CONTENT_HEIGHT,
  onItemsRendered = () => {},
  onClientHeightChange = () => {},
  tableProps = {},
  isFixedSize = false,
  useResize = true,
  useSelect = false,
  searchContainerRef,
  setSelectedRows = () => {},
  setRowsCount = () => {},
  useExpand = false,
  useHighlight = false,
  highlightedRows,
  useGrouping = false,
  defaultGroupBy,
  defaultSortBy,
  alwaysExpanded = false,
  scrollToRow,
  showFooter = false,
  style,
  sortBy: propSortBy,
  setSortBy: propSetSortBy = () => {},
  hiddenColumns,
  stateKey,
  aggregations: propAggregations,
  setFilters = () => {},
  groupBy: propGroupBy,
  setGroupBy: propSetGroupBy = () => {},
  maxColumnWidth = DEFAULT_MAX_COLUMN_WIDTH,
  hideSelectAll = false
}) => {
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 50,
      width: 150,
      maxWidth: maxColumnWidth,
      disableFilters: true,
    }),
    [maxColumnWidth]
  );

  const filterTypes = React.useMemo(
    () => ({
      includesOption: includesOptionFilter,
      dateRange: dateRangeFilter,
      isNotEmpty,
    }),
    []
  );

  const sortTypes = React.useMemo(
    () => ({
      alphanumeric: defaultSort,
      objectLabel: sortByLabel,
      number: numberSort,
    }),
    []
  );

  const aggregations = React.useMemo(
    () => ({
      ...defaultAggregations,
      ...propAggregations,
    }),
    [propAggregations]
  );

  const listRef = React.useRef();
  const rowHeights = React.useRef({});
  const cellHeights = React.useRef({});
  const tableRef = React.useRef();
  const listOuterRef = React.useRef();
  const listInnerRef = React.useRef();
  const tableHeaderRef = React.useRef();
  const tableFooterRef = React.useRef();
  const selectedOriginalIdsRef = React.useRef([]);

  const positionFooter = () => {
    if (listOuterRef.current && listInnerRef.current) {
      const outerHeight = listOuterRef.current.clientHeight;
      const innerHeight = listInnerRef.current.offsetHeight;

      const footer = jQuery(tableRef.current).find(".footer-row");
      const css =
        innerHeight < outerHeight
          ? {
              top:
                innerHeight +
                jQuery(tableRef.current)
                  .find(".header-row")
                  .map((i, a) => $(a).outerHeight(true))
                  .toArray()
                  .reduce((num, sum) => (sum += num)),
              bottom: "",
            }
          : { bottom: 0, top: "" };

      footer.css(css);
    }
  };

  const debouncedPositionFooter = _.debounce(positionFooter, 300);

  const setRowHeight = (index, size) => {
    listRef.current.resetAfterIndex(index);
    // Use internal table id for heights
    const { id } = preFilteredRows[index];
    rowHeights.current = { ...rowHeights.current, [id]: size };
    debouncedPositionFooter();
  };

  const setCellHeight = (rowIndex, colId, size) => {
    const { id } = preFilteredRows[rowIndex];
    cellHeights.current = {
      ...cellHeights.current,
      [id]: { ...(cellHeights.current[id] || {}), [colId]: size },
    };

    const heights = Object.values(cellHeights.current[id]).filter(Boolean);
    const rowHeight = heights.length > 0 ? Math.max(...heights) : undefined;
    if (rowHeights.current[id] != rowHeight) {
      setRowHeight(rowIndex, rowHeight);
    }
  };

  const plugins = React.useMemo(
    () =>
      [
        useResize ? useResizeColumns : null,
        useBlockLayout,
        useFilters,
        searchContainerRef ? useGlobalFilter : null,
        useGrouping ? useGroupBy : null,
        useSortBy,
        useSelect ? useRowSelect : null,
        useSelect
          ? (hooks) => {
              hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                  id: "selection",
                  disableGlobalFilter: true,
                  disableResizing: true,
                  minWidth: SELECTION_COLUMN_WIDTH,
                  width: SELECTION_COLUMN_WIDTH,
                  maxWidth: SELECTION_COLUMN_WIDTH,
                  classes: ({ row, isRowSelectable = () => true, disableSelectionCol }) =>
                    `text-center selection ${
                      isRowSelectable(row.original) && !disableSelectionCol ? "" : "disabled"
                    }`,
                  headerClasses: "selection text-center",
                  // The header can use the table's getToggleAllRowsSelectedProps method
                  // to render a checkbox
                  Header: ({ getToggleAllRowsSelectedProps }) => (
                    <div>
                      {!hideSelectAll && <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />}
                    </div>
                  ),
                  // The cell can use the individual row's getToggleRowSelectedProps method
                  // to the render a checkbox
                  Cell: ({ row, isRowSelectable = () => true, disableSelectionCol }) => (
                    <div>
                      <IndeterminateCheckbox
                        key={row.getToggleRowSelectedProps().checked}
                        {...row.getToggleRowSelectedProps()}
                        disabled={!isRowSelectable(row.original) || disableSelectionCol}
                      />
                    </div>
                  ),
                  cellEvents: actionCellEvents(),
                },
                ...columns,
              ]);
            }
          : null,
        useExpand ? useExpanded : null,
      ].filter((p) => p),
    [useResize, useSelect, searchContainerRef, useExpand]
  );

  const [savedColumnWidths, setSavedColumnWidths] = useLocalStorage(
    REACT_TABLE_STORAGE_KEY,
    {},
    stateKey
  );

  // Approximate the column width based on the text content length
  // Get's the longest cell/header item & calculates using magicnumber
  const getColumnWidth = React.useCallback(
    ({ accessor, Header, widthOffset = 0, cellText = (v) => v, headerTextForWidth }) => {
      let accessorFunc = accessor;
      if (typeof accessor !== "function") {
        accessorFunc = (d) => d[accessor];
      }
      const magicSpacing = 8;
      const cellPadding = 8;
      if (typeof Header !== "string") Header = headerTextForWidth || ""; // Hack fix for component headers to prevent error
      const cellLength = Math.max(
        Math.max(
          ...data.map((row) => `${accessorFunc ? cellText(accessorFunc(row)) || "-" : "-"}`.length),
          0
        ) + widthOffset,
        // Headers may be long and wrap, get the width of the longest word to keep the column width small.
        Math.max(
          ...(typeof Header == "string" ? Header : "" || "").split(" ").map((w) => w.length)
        ) + SORT_TOGGLE_WIDTH
      );
      return Math.min(maxColumnWidth, cellLength * magicSpacing) + cellPadding * 2;
    },
    [data, maxColumnWidth]
  );

  const columns = React.useMemo(
    () =>
      propColumns.map((c) => ({
        ...c,
        width:
          savedColumnWidths[
            c.id ||
              (typeof c.accessor === "string" || c.accessor instanceof String
                ? c.accessor
                : c.Header)
          ] ||
          c.width ||
          getColumnWidth(c),
        ...(c.columns
          ? {
              columns: c.columns.map((sc) => ({
                ...sc,
                width:
                  savedColumnWidths[
                    sc.id ||
                      (typeof sc.accessor === "string" || sc.accessor instanceof String
                        ? sc.accessor
                        : sc.Header)
                  ] ||
                  sc.width ||
                  getColumnWidth(sc),
              })),
            }
          : {}),
      })),
    [propColumns, getColumnWidth]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerProps,
    headerGroups,
    footerGroups,
    rows,
    flatRows,
    prepareRow,
    preFilteredRows,
    setGlobalFilter,
    setGroupBy,
    setSortBy,
    toggleRowSelected,
    toggleAllRowsSelected,
    toggleAllRowsExpanded,
    selectedFlatRows,
    setHiddenColumns,
    setAllFilters,
    state: { globalFilter, groupBy, expanded, columnWidths, filters, sortBy },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      disableSortRemove: true,
      autoResetSortBy: false,
      autoResetFilters: false,
      ...tableProps,
      setRowHeight,
      setCellHeight,
      filterTypes,
      sortTypes,
      aggregations,
    },
    ...plugins
  );

  useEffect(() => {
    if (sortBy) {
      propSetSortBy(sortBy);
    }
  }, [sortBy]);

  useEffect(() => {
    if (filters) {
      setFilters(filters);
    }
  }, [filters]);

  useEffect(() => {
    if (rows) {
      setRowsCount(rows.length);
    }
  }, [rows.length]);

  useEffect(() => {
    setSavedColumnWidths({ ...savedColumnWidths, ...columnWidths });
  }, [columnWidths]);

  useEffect(() => {
    if (hiddenColumns) {
      setHiddenColumns(hiddenColumns);
    }
  }, [hiddenColumns, rows]);

  useEffect(() => {
    if (propSortBy) {
      setSortBy(propSortBy);
    }
  }, [propSortBy]);

  useEffect(() => {
    if (defaultGroupBy) {
      setGroupBy(defaultGroupBy.groupBy);
    }
  }, [defaultGroupBy]);

  useEffect(() => {
    if (defaultSortBy) {
      setSortBy(defaultSortBy);
    }
  }, [defaultSortBy]);

  useEffect(() => {
    if (propGroupBy) {
      setGroupBy(propGroupBy.groupBy);
    }
  }, [propGroupBy]);

  useEffect(() => {
    const savedFilter = localStorageWithExpiry.getItem(`${REACT_TABLE_FILTER_STORAGE_KEY}_${window.location.pathname}`)
    if(savedFilter){
      // Disabled as this causes issues when tables are loaded async,
      // and multiple tables reside on same window.location.pathname (Reports with indicator value sets etc.)
      // Maybe the cache key should be provided as prop
      // setAllFilters(JSON.parse(savedFilter))
    }
  },[]);

  useEffect(() => {
    if (alwaysExpanded && flatRows.some((r) => !r.isExpanded)) {
      toggleAllRowsExpanded(true);
    }
  }, [groupBy, rows]);

  // Row selection gets messed up when data changes. (e.g. adding a new row)
  useEffect(() => {
    // Unless selection will be reset anyway
    if (tableProps.autoResetSelectedRows === false) {
      // Check if there are selected rows which should not be selected.
      if (selectedFlatRows.some((r) => !selectedOriginalIdsRef.current.includes(r.original.id))) {
        // Unselect all
        toggleAllRowsSelected(false);
        // Then select all rows which should be selected.
        selectedOriginalIdsRef.current.forEach((id) => {
          const { toggleRowSelected = () => {} } = preFilteredRows.find((r) => r.original.id == id);
          toggleRowSelected(true);
        });
      }
    }
  }, [data]);

  useEffect(() => {
    if (
      tableProps.isSelectedAllowed &&
      (selectedFlatRows || []).some((r) => !tableProps.isSelectedAllowed(r.original))
    ) {
      toggleRowSelected(
        selectedFlatRows.find((r) => !tableProps.isSelectedAllowed(r.original)).id,
        false
      );
    } else if (
      tableProps.alwaysSelectedRowIds &&
      tableProps.alwaysSelectedRowIds.some(
        (id) => !selectedFlatRows.find((r) => r.original.id == id)
      )
    ) {
      toggleRowSelected(
        tableProps.alwaysSelectedRowIds.find(
          (id) => !selectedFlatRows.find((r) => r.original.id == id)
        ),
        true
      );
    } else {
      setSelectedRows((selectedFlatRows || []).map((row) => row.original));
      // Update selected ids whenever selected flat rows change
      selectedOriginalIdsRef.current = (selectedFlatRows || []).map((r) => r.original.id);
    }
  }, [setSelectedRows, selectedFlatRows]);

  const handleHeightClientChange = () => {
    onClientHeightChange(
      rows.length * (itemDefaultSize + ROW_HEIGHT_PADDING) +
        (tableHeaderRef.current?.scrollHeight || itemDefaultSize) +
        (showFooter ? tableFooterRef.current?.offsetHeight || itemDefaultSize : 0)
    );
  };

  useLayoutEffect(() => {
    handleHeightClientChange();
  }, [rows.length, tableHeaderRef.current, tableFooterRef.current]);

  const getRowHeight = (index) => {
    // Use internal table id for heights
    const { id } = rows[index];
    return (rowHeights.current[id] || itemDefaultSize) + ROW_HEIGHT_PADDING;
  };

  useEffect(() => {
    // Refresh heights when table gets sorted or filtered
    if (listRef.current && !isFixedSize) listRef.current.resetAfterIndex(0);
  }, [rows]);

  const findRowIndex = ({ depth, ...row }) => {
    return flatRows.findIndex(
      (r) => r.depth == depth && Object.keys(row).every((k) => row[k] == r.original[k])
    );
  };

  const shouldScroll = React.useRef(false);

  // Only scroll once until we get new 'scrollToRow' object
  useEffect(() => {
    shouldScroll.current = !!scrollToRow;
  }, [scrollToRow]);

  useEffect(() => {
    // Scrolling can be done once the element is found
    if (scrollToRow && shouldScroll.current) {
      const index = findRowIndex(scrollToRow);
      if (index != -1) {
        shouldScroll.current = false;
        listRef.current.scrollToItem(index);
      }
    }
  }, [rows, scrollToRow]);

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const rowRef = React.useRef({});
      const row = rows[index];
      prepareRow(row);

      const currentHighlight = row?.original?.highlight || row?.original?.status_highlight

      const rowProps = {
        [rowElem ? "rowRef" : "ref"]: rowRef,
        ...row.getRowProps({ style }),
        // Possibility to highlight rows, 'useHighlight' can be used to toggle highlight on/off
        className: `tr ${index % 2 == 0 ? "even" : "odd"} ${
          useHighlight && (currentHighlight || (highlightedRows || {})[row.original.id])
            ? `highlight ${
                typeof currentHighlight == "string" ? currentHighlight : ""
              } ${
                typeof (highlightedRows || {})[row.original.id] == "string"
                  ? highlightedRows[row.original.id]
                  : ""
              }`
            : ""
        } ${row.isGrouped ? "grouped-row" : ""} ${
          row.original?.rowStatus == FORM_STATUS.ADD ? "new-row" : ""
        }`,
        ...(rowElem ? { row: row.original } : {}),
      };

      const rowChildren = row.cells.map((cell) => {
        const { classes = "" } = cell.column;
        const className =
          typeof classes == "function"
            ? classes({ row, ...tableProps, isPlaceholder: cell.isPlaceholder })
            : classes;

        const cellProps = {
          ...cell.getCellProps(),
          className: `td ${className}`,
          ...(cell.column.cellEvents || {}),
          onClick: cell.column.cellEvents?.onClick
            ? (e) => cell.column.cellEvents.onClick(e, row)
            : null,
        };

        return (
          <div {...cellProps}>
            {cell.isAggregated
              ? cell.render("Aggregated")
              : cell.isPlaceholder
              ? ""
              : cell.render("Cell")}
          </div>
        );
      });

      return React.createElement(rowElem || "div", rowProps, rowChildren);
    },
    [prepareRow, rows, rowElem, useHighlight, highlightedRows]
  );

  // Called when window gets resized
  // Calling debounced methods to avoid unnecessary calculations while resize is ongoing
  const onResize = () => {
    if (showFooter) debouncedPositionFooter();
  };

  // onRender takes care of style adjustments after the list has been rendered
  // This won't be called when resizing the window
  const onRender = () => {
    if (showFooter) positionFooter();
    handleHeightClientChange();
  };

  useEffect(() => {
    onResize();

    jQuery(window).on("resize", onResize);

    return () => {
      jQuery(window).off("resize", onResize);
    };
  });

  const handleItemsRenderered = () => {
    // Call props method
    onItemsRendered();
    // Call onRender
    onRender();
  };

  // Render the UI for your table
  return (
    <div ref={tableRef} {...getTableProps({ style })} className="react-table">
      <Scrollbars hideTracksWhenNotNeeded={true}>
        <AutoSizer>
          {({ height, width }) => {
            const tableWidth = Math.max(
              parseInt((headerGroups || [])[0]?.getHeaderGroupProps({})?.style?.width || "0", 10),
              width - AUTOSIZER_MARGIN // Use the full width as minimum.
            );

            const listProps = {
              ref: listRef,
              outerRef: listOuterRef,
              innerRef: listInnerRef,
              // Max height - header height - footer height
              height:
                height -
                (tableHeaderRef.current?.offsetHeight || 0) -
                (tableFooterRef.current?.offsetHeight || 0) -
                AUTOSIZER_MARGIN,
              itemCount: rows.length,
              itemSize: isFixedSize ? itemDefaultSize + ROW_HEIGHT_PADDING : getRowHeight,
              width: tableWidth,
              onItemsRendered: handleItemsRenderered,
              style: { overflowX: "hidden" },
              children: RenderRow,
            };
            return (
              <>
                {searchContainerRef && (
                  <GlobalFilterPortal
                    searchContainerRef={searchContainerRef}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                  />
                )}
                <div ref={tableHeaderRef} style={{ width: "100%" }}>
                  {headerGroups.map(
                    (headerGroup) =>
                      // Render header row only, if the Header of the first column is present to avoid unnecessary groups to be rendered
                      headerGroup.headers[0]?.Header && (
                        <div
                          {...headerGroup.getHeaderGroupProps({})}
                          style={{
                            ...(headerGroup.getHeaderGroupProps({})?.style || {}),
                            width: tableWidth,
                          }}
                          className="tr header-row"
                        >
                          {headerGroup.headers.map((column) => (
                            <div
                              {...column.getHeaderProps(headerProps)}
                              className={`th ${column.headerClasses || ""} ${
                                column.Filter ? "with-filters" : ""
                              }`}
                            >
                              <div {...column.getSortByToggleProps()}>
                                {column.render("Header")}
                                {/* Add a sort direction indicator */}
                                {column.canSort ? (
                                  <span className="sort-toggle pull-right mr-1">
                                    <i
                                      className={`fa fa-${
                                        column.isSortedDesc
                                          ? "sort-down"
                                          : column.isSorted
                                          ? "sort-up"
                                          : "sort"
                                      }`}
                                    />
                                  </span>
                                ) : null}
                              </div>
                              {column.Filter ? column.render("Filter") : null}
                              {/* Use column.getResizerProps to hook up the events correctly */}
                              {useResize && column.canResize && (
                                <div
                                  {...column.getResizerProps()}
                                  className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                                >
                                  <span></span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )
                  )}
                </div>

                <div {...getTableBodyProps()}>
                  {isFixedSize ? (
                    <FixedSizeList {...listProps} outerElementType={CustomScrollbarsVirtualList} />
                  ) : (
                    <VariableSizeList
                      {...listProps}
                      outerElementType={CustomScrollbarsVirtualList}
                    />
                  )}
                </div>

                {showFooter ? (
                  <div>
                    {/* Currently support for only one footer row */}
                    {footerGroups.slice(0, 1).map((group, i) => (
                      <div
                        ref={tableFooterRef}
                        {...group.getFooterGroupProps({})}
                        className="tr footer-row"
                        style={{
                          ...(group.getFooterGroupProps({})?.style || {}),
                          width: tableWidth,
                        }}
                      >
                        {group.headers.map((column) => (
                          <div {...column.getFooterProps()} className="td">
                            {column.Footer ? column.render("Footer") : null}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            );
          }}
        </AutoSizer>
      </Scrollbars>
    </div>
  );
}

export default Table;